import { ModelStats, ModelStatsMap, AccountOverviewData, OpenPosition } from '../types/analytics.types';
import { MODEL_KEYS, MODEL_DISPLAY_NAMES, MODEL_LABELS, MANUAL_ACCOUNT_ID } from '../config/analytics';
import { formatDuration, parseUTC } from './formatters';

const createEmptyStats = (): ModelStats => ({
  totalTrades: 0, totalPL: 0, totalLosses: 0, wins: 0, losses: 0, winRate: 0,
  avgPerWin: 0, avgPerLoss: 0, profitFactor: 0, largestWin: 0, largestLoss: 0,
  totalWinAmount: 0, totalLossAmount: 0, avgReturnPerTrade: 0, expectancy: 0,
  riskRewardRatio: 0, totalWinHoldingTime: 0, totalLossHoldingTime: 0,
  totalHoldingTime: 0, avgHoldingTimeWins: 0, avgHoldingTimeLosses: 0, avgHoldingTimeAll: 0,
  avgReturnPerUnit: 0, avgWinPerUnit: 0, avgLossPerUnit: 0,
});

/**
 * Classify a trade into a model stats key based on timeframe and ml_model_version.
 */
export const getModelStatsKey = (trade: any): string | null => {
  const entryReason = trade.entry_reason || trade.entryReason || '';
  if (entryReason.startsWith('manual')) return 'MANUAL';

  const accountId = trade.account_id || trade.accountId || '';
  const isManualTrade = accountId === MANUAL_ACCOUNT_ID;
  if (isManualTrade) return 'MANUAL';

  const timeframe = trade.timeframe || '';

  if (timeframe === 'HOUR_MFE_ML') {
    return 'H1_MFE_ML';
  }

  if (timeframe === 'HOUR_EVENT_ENTRY') {
    const modelVersion = trade.ml_model_version || trade.mlModelVersion || '';
    // Try ml_model_version first
    if (modelVersion.startsWith('GASBERT')) return 'GASBERT';
    if (modelVersion.startsWith('USOII')) return 'USOII';
    if (modelVersion.startsWith('LOI')) return 'LOI';
    if (modelVersion.startsWith('GOI')) return 'GOI';
    if (modelVersion.startsWith('SILVIO')) return 'SILVIO';
    if (modelVersion.startsWith('RUS')) return 'RUS';
    if (modelVersion.startsWith('QUINI')) return 'QUINI';
    if (modelVersion.startsWith('LIBRA')) return 'LIBRA';
    if (modelVersion.startsWith('COOP')) return 'COOP';
    if (modelVersion.startsWith('YENI')) return 'YENI';
    if (modelVersion.startsWith('DUS')) return 'DUS';

    // Fallback: classify by instrument when ml_model_version is not available
    const instrument = (trade.instrument || '').toUpperCase();
    if (instrument.includes('US100') || instrument.includes('US 100')) return 'USOII';
    if (instrument.includes('NATURALGAS') || instrument.includes('NATURAL_GAS')) return 'GASBERT';
    if (instrument.includes('OIL')) return 'LOI';
    if (instrument.includes('GOLD') || instrument.includes('XAU')) return 'GOI';
    if (instrument.includes('SILVER') || instrument.includes('XAG')) return 'SILVIO';
    if (instrument.includes('EURUSD') || instrument.includes('EUR/USD')) return 'RUS';
    if (instrument.includes('US500') || instrument.includes('US 500')) return 'QUINI';
    if (instrument.includes('GBPUSD') || instrument.includes('GBP/USD')) return 'LIBRA';
    if (instrument.includes('COPPER')) return 'COOP';
    if (instrument.includes('USDJPY') || instrument.includes('USD/JPY')) return 'YENI';
    if (instrument.includes('AUDUSD') || instrument.includes('AUD/USD')) return 'DUS';
  }

  return null;
};

/**
 * Filter trades by date range based on entry_time.
 * Supports both date-only strings ("2026-03-20") and full ISO timestamps.
 * Date-only strings get expanded to midnight/end-of-day local time.
 * Full ISO timestamps (containing 'T') are used as exact UTC boundaries.
 */
export const filterTradesByDateRange = (trades: any[], from?: string, to?: string): any[] => {
  let filtered = trades;

  if (from) {
    const fromDate = new Date(from);
    if (!from.includes('T')) fromDate.setHours(0, 0, 0, 0);
    filtered = filtered.filter((t: any) => parseUTC(t.entry_time) >= fromDate);
  }
  if (to) {
    const toDate = new Date(to);
    if (!to.includes('T')) toDate.setHours(23, 59, 59, 999);
    filtered = filtered.filter((t: any) => parseUTC(t.entry_time) <= toDate);
  }

  return filtered;
};

/**
 * Get ML closed trades (exclude manual account, keep only CLOSED with profit_loss).
 */
export const getMLClosedTrades = (allTrades: any[]): any[] => {
  return allTrades.filter((t: any) => {
    const accountId = t.account_id || t.accountId || '';
    return accountId !== MANUAL_ACCOUNT_ID &&
      t.status === 'CLOSED' &&
      t.profit_loss !== null &&
      t.profit_loss !== undefined;
  });
};

/**
 * Compute per-model stats from an array of closed ML trades.
 */
export const computeModelStats = (closedMLTrades: any[]): ModelStatsMap => {
  const statsMap: ModelStatsMap = {};
  MODEL_KEYS.forEach(key => {
    statsMap[key] = createEmptyStats();
  });

  closedMLTrades.forEach((trade: any) => {
    const statsKey = getModelStatsKey(trade);
    if (!statsKey || !statsMap[statsKey]) return;

    const pl = trade.profit_loss || 0;
    const size = trade.size || 1;
    const normalizedPL = pl / size;
    const stats = statsMap[statsKey];

    stats.totalTrades++;
    stats.totalPL += pl;

    // Holding time
    let holdMin = 0;
    if (trade.entry_time && trade.exit_time) {
      holdMin = (parseUTC(trade.exit_time).getTime() - parseUTC(trade.entry_time).getTime()) / (1000 * 60);
    }
    stats.totalHoldingTime += holdMin;

    if (pl > 0) {
      stats.wins++;
      stats.totalWinAmount += pl;
      stats.totalWinHoldingTime += holdMin;
      if (pl > stats.largestWin) stats.largestWin = pl;
    } else if (pl < 0) {
      stats.losses++;
      stats.totalLosses += pl;
      stats.totalLossAmount += pl;
      stats.totalLossHoldingTime += holdMin;
      if (pl < stats.largestLoss) stats.largestLoss = pl;
    }

    // Per-unit tracking (accumulated, finalized below)
    (stats as any)._totalNormalizedPL = ((stats as any)._totalNormalizedPL || 0) + normalizedPL;
    if (pl > 0) {
      (stats as any)._totalNormalizedWinPL = ((stats as any)._totalNormalizedWinPL || 0) + normalizedPL;
    } else if (pl < 0) {
      (stats as any)._totalNormalizedLossPL = ((stats as any)._totalNormalizedLossPL || 0) + normalizedPL;
    }
  });

  // Finalize derived metrics
  Object.keys(statsMap).forEach(key => {
    const s = statsMap[key];
    if (s.totalTrades > 0) {
      s.winRate = (s.wins / s.totalTrades) * 100;
      s.avgPerWin = s.wins > 0 ? s.totalWinAmount / s.wins : 0;
      s.avgPerLoss = s.losses > 0 ? s.totalLossAmount / s.losses : 0;
      s.profitFactor = s.totalLossAmount !== 0 ? s.totalWinAmount / Math.abs(s.totalLossAmount) : 0;
      s.avgReturnPerTrade = s.totalPL / s.totalTrades;
      const lossRate = s.losses / s.totalTrades;
      s.expectancy = (s.winRate / 100 * s.avgPerWin) - (lossRate * Math.abs(s.avgPerLoss));
      s.riskRewardRatio = s.avgPerLoss !== 0 ? s.avgPerWin / Math.abs(s.avgPerLoss) : 0;
      s.avgHoldingTimeWins = s.wins > 0 ? s.totalWinHoldingTime / s.wins : 0;
      s.avgHoldingTimeLosses = s.losses > 0 ? s.totalLossHoldingTime / s.losses : 0;
      s.avgHoldingTimeAll = s.totalHoldingTime / s.totalTrades;

      // Per-unit metrics
      const totalNorm = (s as any)._totalNormalizedPL || 0;
      const totalNormWin = (s as any)._totalNormalizedWinPL || 0;
      const totalNormLoss = (s as any)._totalNormalizedLossPL || 0;
      s.avgReturnPerUnit = totalNorm / s.totalTrades;
      s.avgWinPerUnit = s.wins > 0 ? totalNormWin / s.wins : 0;
      s.avgLossPerUnit = s.losses > 0 ? totalNormLoss / s.losses : 0;
    }

    // Clean up temp fields
    delete (s as any)._totalNormalizedPL;
    delete (s as any)._totalNormalizedWinPL;
    delete (s as any)._totalNormalizedLossPL;
  });

  return statsMap;
};

/**
 * Compute account overview from model stats map.
 */
export const computeAccountOverview = (statsMap: ModelStatsMap, initialCapital: number): AccountOverviewData => {
  let totalTrades = 0, wins = 0, losses = 0, netPL = 0;

  MODEL_KEYS.forEach(key => {
    const s = statsMap[key];
    if (s && s.totalTrades > 0) {
      totalTrades += s.totalTrades;
      wins += s.wins;
      losses += s.losses;
      netPL += s.totalPL;
    }
  });

  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  const currentBalance = initialCapital + netPL;
  const returnPct = initialCapital > 0 ? (netPL / initialCapital) * 100 : 0;

  return { initialCapital, currentBalance, netPL, returnPct, totalTrades, wins, losses, winRate };
};

/**
 * Get model display name from raw trade data.
 */
export const getModelDisplayName = (modelVersion: string | undefined, timeframe: string | undefined): string => {
  if (timeframe === 'HOUR_MFE_ML') return 'H1 MFE ML';
  if (modelVersion && MODEL_DISPLAY_NAMES[modelVersion]) return MODEL_DISPLAY_NAMES[modelVersion];
  return modelVersion || timeframe || 'Unknown';
};

/**
 * Extract open positions with friendly model names and duration.
 */
export const getOpenPositions = (allTrades: any[]): OpenPosition[] => {
  return allTrades
    .filter((t: any) => t.status === 'OPEN')
    .map((t: any) => {
      let model = getModelDisplayName(t.ml_model_version || t.mlModelVersion, t.timeframe);
      // Fallback: resolve from instrument when ml_model_version is missing
      if (model === 'HOUR_EVENT_ENTRY') {
        const key = getModelStatsKey(t);
        if (key) model = MODEL_LABELS[key] || key;
      }
      return {
        trade_id: t.trade_id || t.tradingview_trade_id || '',
        model,
        instrument: t.instrument || '',
        direction: t.direction || '',
        entry_price: t.entry_price || 0,
        entry_time: t.entry_time || '',
        size: t.size || 0,
        duration: t.entry_time ? formatDuration(t.entry_time) : '-',
      };
    });
};

/**
 * Get the position size for a model from its first trade.
 */
export const getModelSize = (closedTrades: any[], modelKey: string): number => {
  const trade = closedTrades.find((t: any) => getModelStatsKey(t) === modelKey);
  return trade?.size || 0;
};
